// Microsoft (Entra ID) SSO + Graph for JC Steele staff (@jcsteele.com).
// Acquires a Microsoft ID token in the browser via MSAL; the backend (/account/MicrosoftToken)
// validates it and returns the portal's own JWT. Also acquires a delegated Microsoft Graph token
// (Mail.Read) for the opt-in "Email Insights" feature on Sales Insights.
//
// Config-driven: clientId + authority come from the per-environment appsettings.json (the same
// never-copied file that holds API_URL), with the original single shared app registration kept as a
// hardcoded FALLBACK so a missing key can never break sign-in. The SAME single-tenant app serves both
// sandbox and production — redirectUri is window.location.origin, which matches the SPA redirect URIs
// registered in Entra (https://sandbox.jcsteeleservice.com and https://www.jcsteeleservice.com).
// No client secret is used (public SPA + PKCE).
import { PublicClientApplication } from "@azure/msal-browser";
import config from "@/assets/js/appsettings.json";

const CLIENT_ID = (config && config.AZURE_CLIENT_ID) || "38942276-bf56-409d-97ab-41e9c2b45efc";
const AUTHORITY = (config && config.AZURE_AUTHORITY) ||
  "https://login.microsoftonline.com/9e55f18c-255b-46b0-9164-c189ea38288d";

const msalInstance = new PublicClientApplication({
  auth: {
    clientId: CLIENT_ID,
    authority: AUTHORITY,
    redirectUri: window.location.origin,
    navigateToLoginRequestUrl: false,
  },
  cache: { cacheLocation: "sessionStorage", storeAuthStateInCookie: false },
});

// msal-browser v3 requires initialize() before any call; v2 has no such method. Guard for both.
let _ready = null;
function ready() {
  if (!_ready) {
    _ready =
      typeof msalInstance.initialize === "function"
        ? msalInstance.initialize()
        : Promise.resolve();
  }
  return _ready;
}

// Opens the Microsoft sign-in popup and returns the raw ID token (JWT) to hand to the backend.
export async function signInWithMicrosoft() {
  await ready();
  const result = await msalInstance.loginPopup({
    scopes: ["openid", "profile", "email"],
    prompt: "select_account",
  });
  if (!result || !result.idToken) {
    throw new Error("Microsoft did not return an ID token.");
  }
  return result.idToken;
}

// ===== Microsoft Graph — Email Insights (delegated Mail.Read) =====
// Least-privilege, read-only. The rep explicitly opts in; the token is acquired silently when consent
// already exists (admin-consented tenant), otherwise a popup surfaces Microsoft's "Allow" prompt.

const GRAPH_MAIL_SCOPES = ["Mail.Read"];

function activeAccount() {
  const accts = msalInstance.getAllAccounts();
  return accts && accts.length ? accts[0] : null;
}

// Acquire a Graph access token for the signed-in rep's mailbox. Silent first; falls back to an
// interactive popup for consent/interaction. If MSAL has no account yet (e.g. the rep logged in with a
// password, not Microsoft), we sign them in interactively first (same @jcsteele.com identity).
export async function getGraphMailToken() {
  await ready();
  let account = activeAccount();
  if (!account) {
    const login = await msalInstance.loginPopup({
      scopes: ["openid", "profile", "email"].concat(GRAPH_MAIL_SCOPES),
      prompt: "select_account",
    });
    account = (login && login.account) || activeAccount();
  }
  try {
    const res = await msalInstance.acquireTokenSilent({ account, scopes: GRAPH_MAIL_SCOPES });
    return res.accessToken;
  } catch (e) {
    const res = await msalInstance.acquireTokenPopup({ account, scopes: GRAPH_MAIL_SCOPES });
    return res.accessToken;
  }
}

// Best-effort check: do we already hold Graph consent so we can read mail without prompting? Used to
// decide whether "Link my email" should auto-load. Never throws.
export async function hasMailConsentSilently() {
  try {
    await ready();
    const account = activeAccount();
    if (!account) return false;
    const res = await msalInstance.acquireTokenSilent({ account, scopes: GRAPH_MAIL_SCOPES });
    return !!(res && res.accessToken);
  } catch (e) {
    return false;
  }
}

// ===== Microsoft Graph — Entra group lookup (delegated Group.Read.All) =====
// Powers Account Setup → Report Access: the Master types a security-group name, we search the
// tenant's groups and resolve the picked groups' members. Group.Read.All requires ADMIN consent —
// the popup surfaces Microsoft's approval prompt on first use (the Masters using this ARE the
// tenant admins; tick "Consent on behalf of your organization" so it only happens once).

const GRAPH_GROUP_SCOPES = ["Group.Read.All"];

export async function getGraphGroupToken() {
  await ready();
  let account = activeAccount();
  if (!account) {
    const login = await msalInstance.loginPopup({
      scopes: ["openid", "profile", "email"].concat(GRAPH_GROUP_SCOPES),
      prompt: "select_account",
    });
    account = (login && login.account) || activeAccount();
  }
  try {
    const res = await msalInstance.acquireTokenSilent({ account, scopes: GRAPH_GROUP_SCOPES });
    return res.accessToken;
  } catch (e) {
    const res = await msalInstance.acquireTokenPopup({ account, scopes: GRAPH_GROUP_SCOPES });
    return res.accessToken;
  }
}

// Search the tenant's groups by display-name prefix. Returns [{ id, name, mail, kind }] (max 12).
// mail + kind ("Security" / "M365" / "Distribution") let the admin tell same-named groups apart —
// tenants often carry a security group AND a distribution list with the same display name, and
// picking the wrong one syncs 0 members.
export async function searchEntraGroups(term) {
  const q = String(term || "").trim().replace(/'/g, "''");   // escape single quotes for the OData filter
  if (q.length < 2) return [];
  const token = await getGraphGroupToken();
  const url =
    "https://graph.microsoft.com/v1.0/groups" +
    "?$filter=" + encodeURIComponent("startswith(displayName,'" + q + "')") +
    "&$select=id,displayName,mail,groupTypes,securityEnabled&$top=12";
  const resp = await fetch(url, { headers: { Authorization: "Bearer " + token } });
  if (!resp.ok) throw new Error("Graph group search failed (" + resp.status + ")");
  const data = await resp.json();
  return (data.value || [])
    .map((g) => ({
      id: g.id,
      name: g.displayName || "",
      mail: g.mail || "",
      kind: (g.groupTypes || []).indexOf("Unified") >= 0 ? "M365" : (g.securityEnabled ? "Security" : "Distribution"),
    }))
    .filter((g) => g.id && g.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

// One paged read of /groups/{id}{path}, splitting USER objects from everything else.
// `scanned` counts EVERY directory object returned (users + nested groups/contacts/devices) so an
// empty user list can be diagnosed: scanned=0 → the group is genuinely empty in Entra.
async function readGroupObjects(token, groupId, path) {
  const users = [];
  let scanned = 0;
  let url =
    "https://graph.microsoft.com/v1.0/groups/" + encodeURIComponent(groupId) + path +
    "?$select=displayName,mail,userPrincipalName&$top=999";
  for (let page = 0; url && page < 20; page++) {           // 20 pages × 999 = plenty; hard stop for safety
    const resp = await fetch(url, { headers: { Authorization: "Bearer " + token } });
    if (!resp.ok) throw new Error("Graph group members failed (" + resp.status + ")");
    const data = await resp.json();
    (data.value || []).forEach((m) => {
      if (!m) return;
      scanned++;
      if (m["@odata.type"] && m["@odata.type"] !== "#microsoft.graph.user") return;
      const email = (m.mail || m.userPrincipalName || "").trim().toLowerCase();
      const name = (m.displayName || "").trim();
      if (email.indexOf("@") > 0 || name) users.push({ name, email });
    });
    url = data["@odata.nextLink"] || null;
  }
  // Paging cap hit with more pages remaining: FAIL rather than return a silently-partial list —
  // the sync full-replaces stored membership, so a truncated read would revoke real users' access.
  if (url) throw new Error("Group membership read truncated at " + scanned + " objects — group is too large to sync.");
  return { users, scanned };
}

// All USER members of a group as { members: [{ name, email }] sorted by name, scanned }.
// transitiveMembers first (nested groups flattened); if that yields NO users, retry the direct
// /members edge — some group types answer one edge but not the other — and log a console
// diagnostic so an unexpected 0 can be traced to what Graph actually returned.
export async function fetchGroupMembersDetailed(groupId) {
  const token = await getGraphGroupToken();
  let r = null;
  let transErr = null;
  try {
    r = await readGroupObjects(token, groupId, "/transitiveMembers");
  } catch (e) {
    transErr = e;   // still try /members below — only throw when BOTH edges fail
  }
  if (!r || !r.users.length) {
    try {
      const direct = await readGroupObjects(token, groupId, "/members");
      if (!r || direct.users.length || direct.scanned > r.scanned) r = direct;
    } catch (e2) {
      if (!r) throw transErr || e2;
    }
  }
  if (!r.users.length)
    console.warn("[Entra] group " + groupId + " returned no user members (directory objects seen: " + r.scanned + ")");
  return {
    members: r.users.sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)),
    scanned: r.scanned,
  };
}

// Back-compat shape: just the sorted member array. Powers the membership SYNC (emails) and any
// caller that doesn't need the diagnostics.
export async function fetchGroupMembers(groupId) {
  return (await fetchGroupMembersDetailed(groupId)).members;
}

// Group metadata for diagnosing an empty membership (what IS this group?). Best-effort caller-side.
export async function fetchGroupInfo(groupId) {
  const token = await getGraphGroupToken();
  const url =
    "https://graph.microsoft.com/v1.0/groups/" + encodeURIComponent(groupId) +
    "?$select=id,displayName,mail,groupTypes,securityEnabled,onPremisesSyncEnabled,membershipRule";
  const resp = await fetch(url, { headers: { Authorization: "Bearer " + token } });
  if (!resp.ok) throw new Error("Graph group info failed (" + resp.status + ")");
  return await resp.json();
}

// Lowercase member emails only — what the access-list sync stores.
export async function fetchGroupMemberEmails(groupId) {
  return (await fetchGroupMembers(groupId))
    .map((m) => m.email)
    .filter((e) => e && e.indexOf("@") > 0);
}

// Fetch the rep's recent Inbox (last `days`, up to `top` messages) as plain-text bodies.
// Returns [{ id, subject, from:{name,address}, to:[…], cc:[…], received, preview, body }].
export async function fetchRecentInbox(days, top) {
  days = days || 30;
  top = top || 50;
  const token = await getGraphMailToken();
  const since = new Date(Date.now() - days * 86400000).toISOString();
  const url =
    "https://graph.microsoft.com/v1.0/me/mailFolders/Inbox/messages" +
    "?$top=" + top +
    "&$select=subject,from,toRecipients,ccRecipients,receivedDateTime,bodyPreview,body" +
    "&$filter=" + encodeURIComponent("receivedDateTime ge " + since) +
    "&$orderby=" + encodeURIComponent("receivedDateTime desc");
  const resp = await fetch(url, {
    headers: {
      Authorization: "Bearer " + token,
      // Ask Graph for a plain-text body (not HTML) — cleaner for the AI matcher + display.
      Prefer: 'outlook.body-content-type="text"',
    },
  });
  if (!resp.ok) throw new Error("Graph inbox request failed (" + resp.status + ")");
  const data = await resp.json();
  const addr = (r) =>
    r && r.emailAddress
      ? { name: r.emailAddress.name || "", address: (r.emailAddress.address || "").toLowerCase() }
      : { name: "", address: "" };
  return (data.value || []).map((m) => ({
    id: m.id,
    subject: m.subject || "(no subject)",
    from: addr(m.from),
    to: (m.toRecipients || []).map(addr),
    cc: (m.ccRecipients || []).map(addr),
    received: m.receivedDateTime,
    preview: m.bodyPreview || "",
    body: m.body && m.body.content ? m.body.content : m.bodyPreview || "",
  }));
}
